import React from 'react';

/**
 * ShiftWagon route-marker mark.
 * Implements workspaces/shiftwagon-web/artifacts/03-logo-spec.md
 *
 * A route marker plate carrying a double chevron — the "keep going this way"
 * signal found on every South African road. Verified legible down to 16px.
 *
 * Variants:
 *   default  — asphalt plate, signal chevrons (on light surfaces)
 *   inverted — signal plate, asphalt chevrons (on asphalt surfaces)
 *   mono     — outlined plate, single colour (print / single-colour contexts)
 */

type LogoVariant = 'default' | 'inverted' | 'mono';

interface LogoMarkProps {
  size?: number;
  variant?: LogoVariant;
  title?: string;
}

export const LogoMark: React.FC<LogoMarkProps> = ({
  size = 40,
  variant = 'default',
  title,
}) => {
  const plateFill =
    variant === 'inverted' ? 'var(--sw-signal)' : variant === 'mono' ? 'none' : 'var(--sw-asphalt)';
  const chevron =
    variant === 'inverted' ? 'var(--sw-asphalt)' : variant === 'mono' ? 'currentColor' : 'var(--sw-signal)';
  const plateStroke = variant === 'mono' ? 'currentColor' : 'none';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <rect
        x="2"
        y="2"
        width="60"
        height="60"
        rx="10"
        fill={plateFill}
        stroke={plateStroke}
        strokeWidth={variant === 'mono' ? 3 : 0}
      />
      <path
        d="M16 20 L27 32 L16 44"
        fill="none"
        stroke={chevron}
        strokeWidth="7"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
      <path
        d="M31 20 L42 32 L31 44"
        fill="none"
        stroke={chevron}
        strokeWidth="7"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  );
};

interface LogoProps {
  size?: number;
  variant?: LogoVariant;
  /** Hide the wordmark and render the plate alone (avatars, tight bars). */
  markOnly?: boolean;
}

const Logo: React.FC<LogoProps> = ({ size = 40, variant = 'default', markOnly = false }) => {
  if (markOnly) {
    return <LogoMark size={size} variant={variant} title="ShiftWagon" />;
  }

  return (
    <span className="sw-logo">
      <LogoMark size={size} variant={variant} />
      <span className="sw-logo-word">shiftwagon</span>
    </span>
  );
};

export default Logo;
